import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MDBContainer } from "mdb-react-ui-kit";
import { usePageState } from "../context/AppStateContext";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";
// eslint-disable-next-line
import { getCurrencySymbol, formatPriceWithCurrency } from "../utils/currency";
import api from "../services/api";
import { fetchOrders } from "../services/ordersApi";
import { uploadFilesToAzure } from "../components/AddProduct/helpers";

import "./ViewProductsPage.css";

const marketplaces = [
  { id: "ebay", name: "eBay", color: "primary" },
  { id: "shopify", name: "Shopify", color: "success" },
  { id: "poshmark", name: "Poshmark", color: "secondary" },
  { id: "vinted", name: "Vinted", color: "info" },
  { id: "depop", name: "Depop", color: "dark" },
];

// ✅ Local SVG placeholder — via.placeholder.com is dead (discontinued), was causing
// ERR_CONNECTION_CLOSED spam in console. This is a data-URI, no network call at all.
const PLACEHOLDER_IMG =
  "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' fill='%23e0e0e0'/%3E%3Ctext x='50%25' y='50%25' font-size='13' text-anchor='middle' fill='%23999' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

// Simple retry image component – tries to reload original src up to 2 times
const RetryImage = ({ src, alt, className }) => {
  const imgRef = useRef(null);
  const retryCount = useRef(0);
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 800; // ms

  const handleError = () => {
    if (retryCount.current < MAX_RETRIES) {
      retryCount.current += 1;
      setTimeout(() => {
        if (imgRef.current) {
          imgRef.current.src =
            src + (src.includes("?") ? "&" : "?") + "t=" + Date.now();
        }
      }, RETRY_DELAY * retryCount.current);
    } else {
      if (imgRef.current) {
        imgRef.current.onerror = null;
        imgRef.current.src = PLACEHOLDER_IMG;
      }
    }
  };

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={handleError}
    />
  );
};

const STATUS_TABS = [
  { key: "", label: "All Products" },
  { key: "pending_approval", label: "Pending Approval" },
  { key: "listed", label: "Listed" },
  { key: "sold", label: "Sold" },
];

const STATUS_TAB_ROLES = ["owner", "admin", "seller manager", "editor"];

const getUserDisplayName = (u) => {
  if (!u) return "";
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return u.storeName || u.store_name || u.name || fullName || u.email || "";
};

const resolveUploadedByName = (product, currentUser) => {
  const fromApi =
    product.uploadedBy?.storeName ||
    product.uploadedBy?.store_name ||
    product.uploadedBy?.name ||
    product.uploaded_by_name ||
    getUserDisplayName(product.uploadedBy);

  if (fromApi) return fromApi;

  const uploadedById = product.uploadedBy?.id ?? product.uploaded_by;
  const currentUserId =
    currentUser?.id || currentUser?.user_id || currentUser?.userId;

  if (
    uploadedById != null &&
    currentUserId != null &&
    String(uploadedById) === String(currentUserId)
  ) {
    return getUserDisplayName(currentUser) || "You";
  }

  return "";
};

// ─────────────────────────────────────────────
// ⚠️ REMOVED: computeVendorPrice(). Pricing is now computed entirely
// server-side by getPricingForRole() (utils/scanPricing.js), same as
// SorterPage.jsx and ProductDetailsPage.jsx. The frontend now only
// ever reads whatever `product.pricing` the backend already decided
// this role should see — no more client-side fleek_cost_floor /
// displayPricePercentage math here.
// ─────────────────────────────────────────────

// Pull a single "headline" price out of whatever the backend sent for
// this role. Mirrors SorterPage.jsx's getHeadlinePrice() exactly.
const getHeadlinePrice = (pricing) => {
  if (!pricing) return null;
  if (
    pricing.price_recommended !== undefined &&
    pricing.price_recommended !== null
  ) {
    return pricing.price_recommended;
  }
  if (pricing.vendor_payout !== undefined && pricing.vendor_payout !== null) {
    return pricing.vendor_payout;
  }
  return null;
};

const isLikelyValidImageUrl = (url) =>
  typeof url === "string" &&
  url.trim().length > 0 &&
  /^https?:\/\//i.test(url.trim()) &&
  !url.includes("placeholder") &&
  !url.startsWith("data:");

const testImageUrl = (url) =>
  new Promise((resolve) => {
    if (!isLikelyValidImageUrl(url)) {
      resolve(false);
      return;
    }
    const img = new Image();
    const timer = setTimeout(() => resolve(false), 6000);
    img.onload = () => {
      clearTimeout(timer);
      resolve(true);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    img.src = url;
  });

const ViewProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ⚠️ REMOVED `displayPricePercentage` from this destructure — pricing
  // is fully server-side now, the frontend never needs this value.
  const { user, companyId } = useAuth();
  const userId = user?.id || user?.user_id || user?.userId;
  const userRoleNormalized = normalizeRole(user?.role);
  const isOwnerOrAdmin = ["owner", "admin"].includes(userRoleNormalized);
  const canSeeStatusTabs = STATUS_TAB_ROLES.includes(userRoleNormalized);
  const isEditor = userRoleNormalized === "editor";
  const statusFilter = searchParams.get("status") || "";
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exclusiveMarketplaceFilters, setExclusiveMarketplaceFilters] =
    useState([]);

  const { state, setState, clearState } = usePageState("viewProducts");
  // eslint-disable-next-line
  const { setState: setAddProductState } = usePageState("addProduct");
  const BASE_URL =
    "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

  const {
    currentPage = 1,
    itemsPerPage = 5,
    searchQuery = "",
    categoryFilter = "",
    marketplaceFilter = "",
    sellerFilter = "",
    selectedProductIds = [],
    expandedProduct = null,
    cachedProducts = null,
    lastFetched = null,
    cachedStatus = null,
    cachedStartDate = null,
    cachedEndDate = null,
  } = state;

  const [products, setProducts] = useState(cachedProducts || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // eslint-disable-next-line
  const [companyCurrency, setCompanyCurrency] = useState("USD");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadingDepop, setBulkUploadingDepop] = useState(false);
  const [orderStatusMap, setOrderStatusMap] = useState({});
  const [userStoreMap, setUserStoreMap] = useState({});

  const [imageEditModalOpen, setImageEditModalOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingImages, setEditingImages] = useState([]);
  const [imageEditSaving, setImageEditSaving] = useState(false);
  const [validatingProductId, setValidatingProductId] = useState(null);
  const [draggedImageId, setDraggedImageId] = useState(null);
  const [dragOverImageId, setDragOverImageId] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    const fetchUsers = async () => {
      try {
        const res = await api.get("/api/users", {
          params: { company_id: companyId },
        });
        const users = res.data?.users || [];
        const map = {};
        users.forEach((u) => {
          if (u && u.id) {
            map[u.id] = u.storeName || u.store_name || "";
          }
        });
        setUserStoreMap(map);
      } catch (err) {
        console.error("Failed to fetch users for store names", err);
        setUserStoreMap({
          66: "Drip Uz",
          40: "AA Vintage",
          49: "VintageHunt",
        });
      }
    };
    fetchUsers();
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !userId) return;
    const fetchOrderStatuses = async () => {
      try {
        const result = await fetchOrders({
          companyId,
          userId,
          userRole: normalizeRole(user?.role),
        });
        const orders = result.data || [];
        const map = {};
        orders.forEach((order) => {
          (order.items || []).forEach((item) => {
            const pid = item.product_id;
            if (pid) {
              map[pid] = {
                seller_status: order.seller_status,
                rider_status: order.rider_status,
                qc_status: order.qc_status,
                outbound_status: order.outbound_status,
                order_id: order.id,
              };
            }
          });
        });
        setOrderStatusMap(map);
      } catch (err) {
        console.error("Failed to fetch order statuses:", err);
      }
    };
    fetchOrderStatuses();
  }, [companyId, userId, user?.role]);

  // ─────────────────────────────────────────────
  // ⚠️ CHANGED: formatPriceInner no longer branches on isOwnerOrAdmin
  // or does any fleek_cost_floor/displayPricePercentage math itself.
  // It just reads product.pricing (already role-scoped by the backend
  // via getPricingForRole) and formats whichever figure applies.
  // ─────────────────────────────────────────────
  const formatPriceInner = useCallback(
    (fallbackAmount, product = null) => {
      const headline = product ? getHeadlinePrice(product.pricing) : null;
      const amount = headline != null ? headline : fallbackAmount;
      return formatPriceWithCurrency(
        amount,
        product?.currency || companyCurrency,
      );
    },
    [companyCurrency],
  );

  const fetchProducts = useCallback(
    async (force = false) => {
      const now = Date.now();
      const CACHE_TTL = 5 * 60 * 1000;

      const cacheIsValid =
        !force &&
        cachedProducts &&
        lastFetched &&
        now - lastFetched < CACHE_TTL &&
        cachedStatus === statusFilter &&
        cachedStartDate === startDate &&
        cachedEndDate === endDate;
      if (cacheIsValid) {
        setProducts(cachedProducts);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data: productsRes } = await api.get("/api/products", {
          params: {
            company_id: companyId,
            user_id: userId,
            // ✅ ADDED: lets the backend compute + attach a role-appropriate
            // `pricing` object per product (getPricingForRole), and strip raw
            // fleek_* fields for restricted roles server-side. See
            // routes/ebayProduct.js GET /api/products.
            role: userRoleNormalized,
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(startDate ? { start_date: startDate } : {}),
            ...(endDate ? { end_date: endDate } : {}),
          },
        });

        const productsData = Array.isArray(productsRes?.data)
          ? productsRes.data
          : productsRes?.data?.products || productsRes?.products || [];

        const productIds = productsData.map((p) => p.id);
        let ebayMap = {};
        let shopifyMap = {};
        let vintedMap = {};
        let depopMap = {};

        if (productIds.length > 0) {
          try {
            const results = await Promise.allSettled([
              api.post("/api/ebay-product-batch", { product_ids: productIds }),
              api.post("/api/shopify-product-batch", {
                product_ids: productIds,
              }),
              api.post("/api/vinted-product-batch", {
                product_ids: productIds,
              }),
              api.post("/api/depop-product-batch", {
                product_ids: productIds,
              }),
            ]);

            if (results[0].status === "fulfilled") {
              ebayMap = results[0].value.data?.data || {};
            } else {
              console.error("eBay batch failed:", results[0].reason);
            }

            if (results[1].status === "fulfilled") {
              shopifyMap = results[1].value.data?.data || {};
            } else {
              console.error("Shopify batch failed:", results[1].reason);
            }

            if (results[2].status === "fulfilled") {
              vintedMap = results[2].value.data?.data || {};
            } else {
              console.error("Vinted batch failed:", results[2].reason);
            }

            if (results[3].status === "fulfilled") {
              depopMap = results[3].value.data?.data || {};
            } else {
              console.error("Depop batch failed:", results[3].reason);
            }
          } catch (err) {
            console.error("Batch fetch error:", err);
          }
        }

        const productsWithMarketplaces = productsData.map((product) => {
          const ebayData =
            ebayMap[product.id] ?? ebayMap[String(product.id)] ?? null;
          const shopifyData =
            shopifyMap[product.id] ?? shopifyMap[String(product.id)] ?? null;
          const vintedData =
            vintedMap[product.id] ?? vintedMap[String(product.id)] ?? null;
          const depopData =
            depopMap[product.id] ?? depopMap[String(product.id)] ?? null;

          const hasEbay =
            ebayData &&
            ebayData.ebay_item_id &&
            ebayData.ebay_item_id !== "Not listed";
          const hasShopify = shopifyData && Object.keys(shopifyData).length > 0;
          const hasVinted = vintedData && Object.keys(vintedData).length > 0;
          const hasDepop = depopData && Object.keys(depopData).length > 0;

          const marketplaceDetails = {};

          const categoryName =
            product.category?.name || product.category || "Other";
          const uploadedByName = resolveUploadedByName(product, user);
          const conditionValue =
            product.condition ||
            product.condition_name ||
            product.item_condition ||
            ebayData?.condition ||
            null;

          // ✅ NOTE: product.pricing already comes from the backend
          // (getPricingForRole) — formatPriceInner reads it directly,
          // so every marketplace field below stays consistent with
          // what this role is allowed to see.
          marketplaceDetails.local = {
            Title: product.title || "",
            Description: product.description || "",
            SKU: product.sku || "",
            Price: formatPriceInner(product.price || 0, product),
            Quantity: product.quantity || 0,
            Category: categoryName,
            Condition: conditionValue || "Not specified",
            ...(uploadedByName ? { "Uploaded By": uploadedByName } : {}),
          };

          if (hasVinted) {
            marketplaceDetails.vinted = {
              "Vinted ID": vintedData.id || "Not synced",
              "Internal Product ID": vintedData.internal_product_id || "",
              Title: vintedData.title || "",
              Description: vintedData.description || "",
              Price: formatPriceInner(vintedData.price || 0, product),
              Category: vintedData.category || "",
              Brand: vintedData.brand || "",
              Size: vintedData.size || "",
              Condition: vintedData.condition || "",
              "Package Size": vintedData.package_size || "",
              Color: vintedData.color || "",
              Status: vintedData.status || "draft",
            };
          }

          if (hasDepop) {
            marketplaceDetails.depop = {
              "Depop ID": depopData.id || "Not synced",
              "Internal Product ID": depopData.internal_product_id || "",
              Title: depopData.title || "",
              Description: depopData.description || "",
              Price: formatPriceInner(depopData.price || 0, product),
              Category: depopData.category || "",
              Brand: depopData.brand || "",
              Size: depopData.size || "",
              Condition: depopData.condition || "",
              Color: depopData.color || "",
              Status: depopData.status || "draft",
            };
          }

          if (hasEbay) {
            const ebayFields = {};

            if (ebayData.aspects) {
              let aspectsObj = ebayData.aspects;
              if (typeof aspectsObj === "string") {
                try {
                  aspectsObj = JSON.parse(aspectsObj);
                } catch {
                  aspectsObj = {};
                }
              }
              Object.entries(aspectsObj || {}).forEach(([key, value]) => {
                const val = Array.isArray(value) ? value.join(", ") : value;
                if (val && val !== "N/A") ebayFields[key] = val;
              });
            }

            ebayFields.SKU = ebayData.sku || product.sku || "";
            ebayFields.Price = formatPriceInner(
              ebayData.price || product.price || 0,
              product,
            );
            ebayFields.Quantity = ebayData.quantity || 0;
            ebayFields.Condition =
              ebayData.condition || product.condition || "";
            ebayFields["Listing Type"] =
              ebayData.listing_type || "Not specified";
            ebayFields.Duration = ebayData.duration || "Not specified";

            marketplaceDetails.ebay = ebayFields;
          }

          if (hasShopify) {
            const shopifyFields = {};
            shopifyFields.SKU = shopifyData.sku || product.sku || "";
            shopifyFields.Price = formatPriceInner(
              shopifyData.price || product.price || 0,
              product,
            );
            shopifyFields.Quantity = shopifyData.quantity || 0;
            shopifyFields.Vendor = shopifyData.vendor || "";
            shopifyFields["Product Type"] =
              shopifyData.product_type || product.category || "";
            shopifyFields.Status = shopifyData.status || "draft";

            marketplaceDetails.shopify = shopifyFields;
          }

          const rawImages = Array.isArray(product.images) ? product.images : [];
          const cleanedImages = rawImages.filter(isLikelyValidImageUrl);

          return {
            id: product.id,
            title: product.title,
            description: product.description,
            price: product.price,
            category: categoryName,
            condition: conditionValue,
            quantity: product.quantity,
            sku: product.sku,
            status: product.status || null,
            uploadedBy: {
              id: product.uploadedBy?.id ?? product.uploaded_by ?? null,
              name: uploadedByName,
              storeName: userStoreMap[product.uploaded_by] || null,
            },
            images: cleanedImages.length
              ? cleanedImages
              : isLikelyValidImageUrl(product.primaryImage)
                ? [product.primaryImage]
                : [PLACEHOLDER_IMG],
            selectedMarketplaces: [
              "local",
              ...(hasEbay ? ["ebay"] : []),
              ...(hasShopify ? ["shopify"] : []),
              ...(hasVinted ? ["vinted"] : []),
              ...(hasDepop ? ["depop"] : []),
            ],
            marketplaceDetails,
            // ✅ CHANGED: keep raw fleek fields around ONLY if the
            // backend actually sent them (it strips them server-side
            // for restricted roles) — components should prefer
            // `pricing` over these directly.
            fleek_cost_floor: product.fleek_cost_floor ?? null,
            fleek_list_price: product.fleek_list_price ?? null,
            // ✅ ADDED: the role-appropriate pricing object straight
            // from the backend (getPricingForRole) — this is now the
            // single source of truth for what price to show.
            pricing: product.pricing || null,
            currency: product.currency || companyCurrency,
          };
        });

        setProducts(productsWithMarketplaces);
        setState({
          cachedProducts: productsWithMarketplaces,
          lastFetched: Date.now(),
          cachedStatus: statusFilter,
          cachedStartDate: startDate,
          cachedEndDate: endDate,
        });
      } catch (err) {
        console.error("API ERROR:", err);
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to load products. Please try again.",
        );
        setProducts([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      companyId,
      userId,
      userRoleNormalized,
      statusFilter,
      startDate,
      endDate,
      cachedProducts,
      lastFetched,
      cachedStatus,
      cachedStartDate,
      cachedEndDate,
      setState,
      formatPriceInner,
    ],
  );

  useEffect(() => {
    if (userId && companyId) {
      fetchProducts();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, companyId, statusFilter, startDate, endDate]);

  const handleStatusTabChange = (statusKey) => {
    if (statusKey) {
      setSearchParams({ status: statusKey });
    } else {
      setSearchParams({});
    }
    setState({ currentPage: 1, selectedProductIds: [] });
  };

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category).filter(Boolean))],
    [products],
  );

  const sellers = useMemo(
    () => [
      ...new Set(
        products
          .map(
            (p) => userStoreMap[p.uploadedBy?.id] || p.uploadedBy?.name || "",
          )
          .filter(Boolean),
      ),
    ],
    [products, userStoreMap],
  );

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return products.filter((product) => {
      const matchesSearch =
        q === "" ||
        product.title?.toLowerCase().includes(q) ||
        product.sku?.toLowerCase().includes(q) ||
        product.description?.toLowerCase().includes(q);

      const matchesCategory =
        categoryFilter === "" || product.category === categoryFilter;
      const matchesMarketplaceDropdown =
        marketplaceFilter === "" ||
        product.selectedMarketplaces.includes(marketplaceFilter);

      const matchesExclusive =
        exclusiveMarketplaceFilters.length === 0 ||
        (() => {
          const productMps = product.selectedMarketplaces.filter(
            (m) => m !== "local",
          );
          if (productMps.length === 0) return false;
          if (productMps.length !== exclusiveMarketplaceFilters.length)
            return false;
          return productMps.every((mp) =>
            exclusiveMarketplaceFilters.includes(mp),
          );
        })();

      const matchesMarketplace = matchesMarketplaceDropdown && matchesExclusive;
      const matchesSeller =
        sellerFilter === "" ||
        (userStoreMap[product.uploadedBy?.id] || product.uploadedBy?.name) ===
          sellerFilter;

      return (
        matchesSearch && matchesCategory && matchesMarketplace && matchesSeller
      );
      // eslint-disable-next-line
    }); // eslint-disable-next-line
  }, [
    products,
    searchQuery,
    categoryFilter,
    marketplaceFilter,
    sellerFilter,
    exclusiveMarketplaceFilters,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / itemsPerPage),
  );
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setState({ currentPage: page });
  };

  const handleSelectProduct = (productId) => {
    const isSelected = selectedProductIds.includes(productId);
    const newSelection = isSelected
      ? selectedProductIds.filter((id) => id !== productId)
      : [...selectedProductIds, productId];
    setState({ selectedProductIds: newSelection });
  };

  // eslint-disable-next-line
  const handleAddProductCopy = async (product) => {
    try {
      const sellerUserId = product.uploadedBy?.id;
      const existingSku = product.sku;
      let finalSku = existingSku;

      try {
        const res = await fetch(
          `${BASE_URL}/api/v1/checkAndGetSku?user_id=${encodeURIComponent(
            sellerUserId,
          )}&existing_sku=${encodeURIComponent(existingSku)}`,
        );
        const data = await res.json();
        if (data.success) {
          finalSku = data.sku;
        }
      } catch (err) {
        console.error("SKU check failed:", err);
      }

      let fullProductData = null;
      try {
        const token = localStorage.getItem("token");
        const fullRes = await fetch(
          `${BASE_URL}/api/product-full/${product.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const fullJson = await fullRes.json();
        if (fullJson.success && fullJson.data) {
          fullProductData = fullJson.data;
        }
      } catch (err) {
        console.error("Full product fetch failed:", err);
      }

      const { product: fullProduct, ebay } = fullProductData || {};

      let savedFormData = {};
      if (fullProduct?.raw_response) {
        try {
          let raw = fullProduct.raw_response;
          if (typeof raw === "string") raw = JSON.parse(raw);
          if (raw.formData) savedFormData = raw.formData;
        } catch (err) {
          console.warn("Failed to parse raw_response", err);
        }
      }

      const ebayFields = {};
      if (ebay && ebay.aspects) {
        let aspects = ebay.aspects;
        if (typeof aspects === "string") aspects = JSON.parse(aspects);
        Object.entries(aspects).forEach(([key, value]) => {
          let val = Array.isArray(value) ? value.join(", ") : value;
          if (val && val !== "N/A" && val.trim() !== "") {
            let mappedKey = key;
            if (key === "Colour") mappedKey = "Color";
            ebayFields[mappedKey] = val;
          }
        });
      }

      const finalBrand =
        ebayFields.Brand || savedFormData.brand || fullProduct?.brand || "";
      const finalColor =
        ebayFields.Color || savedFormData.color || fullProduct?.color || "";
      const finalMaterial =
        ebayFields.Material ||
        savedFormData.material ||
        fullProduct?.material ||
        "";
      const finalTags = savedFormData.tags || fullProduct?.tags || "";
      const finalVariants = savedFormData.variants?.length
        ? savedFormData.variants
        : [{ id: 1, size: ebayFields.Size || "", color: finalColor, sku: "" }];
      const finalCategoryAspects = savedFormData.categoryAspects || [];
      const finalAllCategoryAspects = savedFormData.allCategoryAspects || [];
      const finalCategoryId = savedFormData.categoryId || "";
      const finalConditionScore = savedFormData.condition_score || "";
      const finalConditionSummary = savedFormData.condition_summary || "";
      const finalAuthenticityScore = savedFormData.authenticity_score || "";
      const finalAuthenticitySummary = savedFormData.authenticity_summary || "";
      const finalVintedSize = savedFormData.vintedSize || ebayFields.Size || "";

      const formData = {
        currentStep: 2,
        formData: {
          uploadedImages: fullProduct?.images?.length
            ? fullProduct.images
            : product.images?.filter((img) => !img.includes("placeholder")) ||
              [],
          blurStatuses: [],
          imageProcessing: false,
          backgroundRemoval: false,
          blurDetection: false,
          upscaleImage: false,
          title: fullProduct?.title || product.title || "",
          description: fullProduct?.description || product.description || "",
          sku: finalSku,
          _existing_product_id: product.id,
          price: String(fullProduct?.price ?? product.price ?? ""),
          quantity: String(fullProduct?.quantity ?? product.quantity ?? "1"),
          category: savedFormData.category || product.category || "",
          condition: fullProduct?.condition || product.condition || "",
          brand: finalBrand,
          color: finalColor,
          material: finalMaterial,
          tags: finalTags,
          metafields: savedFormData.metafields || {},
          categoryAspects: finalCategoryAspects,
          allCategoryAspects: finalAllCategoryAspects,
          categoryId: finalCategoryId,
          condition_score: finalConditionScore,
          condition_summary: finalConditionSummary,
          authenticity_score: finalAuthenticityScore,
          authenticity_summary: finalAuthenticitySummary,
          variants: finalVariants,
          vintedSize: finalVintedSize,
          selectedMarketplaces:
            product.selectedMarketplaces?.filter((m) => m !== "local") || [],
          poshmarkCategory: savedFormData.poshmarkCategory || "Women",
          poshmarkSubCategory: savedFormData.poshmarkSubCategory || "",
          vintedCategory: savedFormData.vintedCategory || "",
          vintedCategoryId: savedFormData.vintedCategoryId || "",
          vintedCategoryPath: savedFormData.vintedCategoryPath || [],
          vintedPackageSize: savedFormData.vintedPackageSize || "medium",
          seller_id: sellerUserId,
        },
      };

      const copyId = `copy_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 8)}`;
      sessionStorage.setItem(copyId, JSON.stringify(formData));

      window.open(`/products/add?copyId=${copyId}`, "_blank");
    } catch (error) {
      console.error("Error preparing product copy:", error);
      alert("Kuch galat ho gaya, dubara try karo.");
    }
  };

  const handleSelectAll = () => {
    const allIds = paginatedProducts.map((p) => p.id);
    const allSelected =
      allIds.length > 0 &&
      allIds.every((id) => selectedProductIds.includes(id));

    if (allSelected) {
      setState({
        selectedProductIds: selectedProductIds.filter(
          (id) => !allIds.includes(id),
        ),
      });
    } else {
      setState({
        selectedProductIds: [...new Set([...selectedProductIds, ...allIds])],
      });
    }
  };

  const handleToggleExpand = (productId) => {
    setState({
      expandedProduct: expandedProduct === productId ? null : productId,
    });
  };

  const handleClearFilters = () => {
    clearState();
    setStartDate("");
    setEndDate("");
    setExclusiveMarketplaceFilters([]);
  };

  const handleBulkUploadVinted = async () => {
    if (selectedProductIds.length === 0) return;
    if (!companyId) return;

    setBulkUploading(true);

    try {
      try {
        await fetch(
          "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: userId }),
          },
        );
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } catch (syncErr) {
        console.warn("⚠️ UserId sync failed, proceeding anyway:", syncErr);
      }

      const payload = {
        userId: userId,
        productIds: selectedProductIds,
      };

      const res = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/bulk-upload-vinted",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (data.success) {
        alert(`✅ Bulk upload started! ${data.processed} products queued.`);
        setState({ selectedProductIds: [] });
      } else {
        console.error("❌ Server returned error:", data.error);
        alert("❌ Bulk upload failed: " + data.error);
      }
    } catch (err) {
      console.error("❌ Network error:", err);
      alert("❌ Network error: " + err.message);
    } finally {
      setBulkUploading(false);
    }
  };

  const handleBulkUploadDepop = async () => {
    if (selectedProductIds.length === 0) return;
    if (!companyId) return;

    setBulkUploadingDepop(true);

    try {
      try {
        await fetch(
          "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: userId }),
          },
        );
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } catch (syncErr) {
        console.warn("⚠️ UserId sync failed, proceeding anyway:", syncErr);
      }

      const payload = {
        userId: userId,
        productIds: selectedProductIds,
      };

      const res = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/bulk-upload-depop",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (data.success) {
        const succeeded = data.results?.filter((r) => r.success) || [];
        const failed = data.results?.filter((r) => !r.success) || [];

        if (failed.length > 0) {
          console.error("❌ Depop bulk upload — failed products:", failed);
          alert(
            `⚠️ Depop upload: ${succeeded.length} queued, ${failed.length} FAILED.\n\n` +
              `First failure: Product ${failed[0].productId} — ${failed[0].error}\n\n` +
              `Check browser console for full details.`,
          );
        } else {
          alert(
            `✅ Depop bulk upload started! ${succeeded.length} products queued.`,
          );
        }
        setState({ selectedProductIds: [] });
      } else {
        console.error("❌ Server returned error:", data.error);
        alert("❌ Depop bulk upload failed: " + data.error);
      }
    } catch (err) {
      console.error("❌ Network error:", err);
      alert("❌ Network error: " + err.message);
    } finally {
      setBulkUploadingDepop(false);
    }
  };

  const canEditImages = isEditor || isOwnerOrAdmin;

  const dedupeImagesKeepOrder = (urls) => {
    const seen = new Set();
    return urls.filter((url) => {
      if (!url || seen.has(url)) return false;
      seen.add(url);
      return true;
    });
  };

  const startEditImages = async (product) => {
    const imgs = Array.isArray(product.images) ? product.images : [];

    const candidateImages = dedupeImagesKeepOrder(
      imgs.filter(isLikelyValidImageUrl),
    );

    if (candidateImages.length === 0) {
      alert("No images available for this product.");
      return;
    }

    setValidatingProductId(product.id);
    try {
      const results = await Promise.all(
        candidateImages.map(async (url) => ({
          url,
          ok: await testImageUrl(url),
        })),
      );

      const workingImages = results.filter((r) => r.ok).map((r) => r.url);
      const brokenCount = results.length - workingImages.length;

      if (brokenCount > 0) {
        console.warn(
          `[ViewProductsPage] Skipped ${brokenCount} broken image URL(s) for product ${product.id}`,
          results.filter((r) => !r.ok).map((r) => r.url),
        );
      }

      if (workingImages.length === 0) {
        alert(
          "None of this product's images could be loaded. They may be broken links.",
        );
        return;
      }

      setEditingProductId(product.id);
      setEditingImages(
        workingImages.map((url, i) => ({
          id: `img_${product.id}_${i}_${Date.now()}`,
          url,
          index: i,
          rotated: 0,
        })),
      );
      setImageEditModalOpen(true);
    } finally {
      setValidatingProductId(null);
    }
  };

  const closeImageEditModal = () => {
    setImageEditModalOpen(false);
    setEditingProductId(null);
    setEditingImages([]);
    setImageEditSaving(false);
    setDraggedImageId(null);
    setDragOverImageId(null);
  };

  const handleRemoveImage = (id) => {
    setEditingImages((prev) =>
      prev
        .filter((img) => img.id !== id)
        .map((img, i) => ({ ...img, index: i })),
    );
  };

  const handleModalImageDragStart = (id) => (e) => {
    e.stopPropagation();
    setDraggedImageId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleModalImageDragOver = (id) => (e) => {
    if (!draggedImageId || draggedImageId === id) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDragOverImageId(id);
  };

  const handleModalImageDragLeave = () => setDragOverImageId(null);

  const handleModalImageDrop = (dropId) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverImageId(null);
    if (!draggedImageId || draggedImageId === dropId) {
      setDraggedImageId(null);
      return;
    }
    setEditingImages((prev) => {
      const dragIndex = prev.findIndex((img) => img.id === draggedImageId);
      const dropIndex = prev.findIndex((img) => img.id === dropId);
      if (dragIndex === -1 || dropIndex === -1) return prev;
      const newImages = [...prev];
      const [moved] = newImages.splice(dragIndex, 1);
      newImages.splice(dropIndex, 0, moved);
      return newImages.map((img, i) => ({ ...img, index: i }));
    });
    setDraggedImageId(null);
  };

  const handleModalImageDragEnd = () => {
    setDraggedImageId(null);
    setDragOverImageId(null);
  };

  const rotateImageInModal = async (index, degrees) => {
    const img = editingImages[index];
    if (!img) return;

    try {
      const loadedImage = await new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Failed to load image"));
        image.src = img.url;
      });

      const radians = (degrees * Math.PI) / 180;
      const isVertical = Math.abs(degrees) % 180 !== 0;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = isVertical
        ? loadedImage.naturalHeight
        : loadedImage.naturalWidth;
      canvas.height = isVertical
        ? loadedImage.naturalWidth
        : loadedImage.naturalHeight;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(radians);
      ctx.drawImage(
        loadedImage,
        -loadedImage.naturalWidth / 2,
        -loadedImage.naturalHeight / 2,
      );

      const rotatedBlob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png", 0.9),
      );
      if (!rotatedBlob) throw new Error("Canvas rotation failed");

      const localUrl = URL.createObjectURL(rotatedBlob);
      const rotatedFile = new File(
        [rotatedBlob],
        `rotated-${editingProductId}-${index + 1}.png`,
        { type: "image/png" },
      );

      setEditingImages((prev) => {
        const newImages = [...prev];
        newImages[index] = {
          ...newImages[index],
          url: localUrl,
          pendingFile: rotatedFile,
          rotated: (newImages[index].rotated || 0) + degrees,
        };
        return newImages;
      });
    } catch (err) {
      console.error("[ViewProductsPage] Rotate failed:", err);
      alert("Image rotation failed. Please try again.");
    }
  };

  const handleSaveImageEdits = async () => {
    if (!editingProductId) return;
    setImageEditSaving(true);
    try {
      const uploadPromises = editingImages.map(async (img) => {
        if (img.pendingFile) {
          const urls = await uploadFilesToAzure([img.pendingFile]);
          return urls[0] || img.url;
        }
        return img.url;
      });

      const reorderedUrls = await Promise.all(uploadPromises);

      await api.patch(`/api/v1/products/${editingProductId}/images`, {
        images: reorderedUrls,
      });

      setProducts((prev) =>
        prev.map((p) =>
          p.id === editingProductId ? { ...p, images: reorderedUrls } : p,
        ),
      );
      setState((prev) => ({
        ...prev,
        cachedProducts:
          prev?.cachedProducts?.map((p) =>
            p.id === editingProductId ? { ...p, images: reorderedUrls } : p,
          ) || [],
      }));
      closeImageEditModal();
    } catch (err) {
      console.error("[ViewProductsPage] Image update failed:", err);
      alert("Image update failed. Please try again.");
    } finally {
      setImageEditSaving(false);
    }
  };

  return (
    <MDBContainer fluid className="view-products-container">
      <h2 className="mb-4">View Products</h2>

      {canSeeStatusTabs && (
        <div
          className="status-tabs"
          role="tablist"
          aria-label="Filter products by status"
        >
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key || "all"}
              type="button"
              role="tab"
              aria-selected={statusFilter === tab.key}
              className={`status-tab-btn ${
                statusFilter === tab.key ? "active" : ""
              }`}
              onClick={() => handleStatusTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-item">
            <label htmlFor="search-products">Search</label>
            <input
              id="search-products"
              type="text"
              className="filter-input"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) =>
                setState({ searchQuery: e.target.value, currentPage: 1 })
              }
            />
          </div>

          <div className="filter-item">
            <label htmlFor="category-filter">Category</label>
            <select
              id="category-filter"
              className="filter-select"
              value={categoryFilter}
              onChange={(e) =>
                setState({ categoryFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label htmlFor="marketplace-filter">Marketplace</label>
            <select
              id="marketplace-filter"
              className="filter-select"
              value={marketplaceFilter}
              onChange={(e) =>
                setState({ marketplaceFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Marketplaces</option>
              {marketplaces.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Exclusive Platforms</label>
            <div className="filter-checkboxes">
              {marketplaces
                .filter((m) => m.id !== "local")
                .map((m) => (
                  <label key={m.id} className="filter-checkbox-label">
                    <input
                      type="checkbox"
                      checked={exclusiveMarketplaceFilters.includes(m.id)}
                      onChange={() => {
                        setExclusiveMarketplaceFilters((prev) =>
                          prev.includes(m.id)
                            ? prev.filter((id) => id !== m.id)
                            : [...prev, m.id],
                        );
                      }}
                    />
                    {m.name}
                  </label>
                ))}
            </div>
          </div>

          <div className="filter-item">
            <label htmlFor="seller-filter">Vendor</label>
            <select
              id="seller-filter"
              className="filter-select"
              value={sellerFilter}
              onChange={(e) =>
                setState({ sellerFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Vendors</option>
              {sellers.map((seller) => (
                <option key={seller} value={seller}>
                  {seller}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label htmlFor="items-per-page">Items per page</label>
            <select
              id="items-per-page"
              className="filter-select"
              value={itemsPerPage}
              onChange={(e) =>
                setState({
                  itemsPerPage: parseInt(e.target.value, 10),
                  currentPage: 1,
                })
              }
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
            </select>
          </div>

          {/* ---------- START DATE FILTER ---------- */}
          <div className="filter-item">
            <label htmlFor="start-date-filter">Start Date</label>
            <input
              id="start-date-filter"
              type="date"
              className="filter-input"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setState({ currentPage: 1 });
              }}
            />
          </div>

          {/* ---------- END DATE FILTER ---------- */}
          <div className="filter-item">
            <label htmlFor="end-date-filter">End Date</label>
            <input
              id="end-date-filter"
              type="date"
              className="filter-input"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setState({ currentPage: 1 });
              }}
            />
          </div>

          <div className="filter-item">
            <button
              type="button"
              className="btn btn-outline-primary me-2"
              onClick={() => fetchProducts(true)}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn-secondary clear-filters-btn"
              onClick={handleClearFilters}
              disabled={loading}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {(searchQuery ||
          categoryFilter ||
          marketplaceFilter ||
          sellerFilter ||
          startDate ||
          endDate ||
          exclusiveMarketplaceFilters.length > 0) && (
          <div className="active-filters">
            <small className="text-muted">
              Showing {filteredProducts.length} of {products.length} products
            </small>
            {searchQuery && (
              <span className="filter-badge">Search: "{searchQuery}"</span>
            )}
            {categoryFilter && (
              <span className="filter-badge">Category: {categoryFilter}</span>
            )}
            {marketplaceFilter && (
              <span className="filter-badge">
                Marketplace: {marketplaceFilter}
              </span>
            )}
            {sellerFilter && (
              <span className="filter-badge">Seller: {sellerFilter}</span>
            )}
            {startDate && (
              <span className="filter-badge">Start: {startDate}</span>
            )}
            {endDate && <span className="filter-badge">End: {endDate}</span>}
            {exclusiveMarketplaceFilters.map((mId) => {
              const market = marketplaces.find((m) => m.id === mId);
              return (
                <span key={mId} className="filter-badge">
                  Only: {market?.name || mId}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {selectedProductIds.length > 0 && (
        <div className="selection-bar">
          <span className="selection-count">
            <strong>{selectedProductIds.length}</strong> product(s) selected
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => setState({ selectedProductIds: [] })}
          >
            Clear Selection
          </button>

          <button
            type="button"
            className="btn btn-info btn-sm ms-2"
            onClick={handleBulkUploadVinted}
            disabled={bulkUploading}
          >
            {bulkUploading
              ? "Starting Bulk Upload..."
              : `Bulk Upload to Vinted (${selectedProductIds.length})`}
          </button>

          <button
            type="button"
            className="btn btn-dark btn-sm ms-2"
            onClick={handleBulkUploadDepop}
            disabled={bulkUploadingDepop}
          >
            {bulkUploadingDepop
              ? "Starting Bulk Upload..."
              : `Bulk Upload to Depop (${selectedProductIds.length})`}
          </button>
        </div>
      )}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={() => fetchProducts(true)}
          >
            Retry
          </button>
        </div>
      )}

      {imageEditModalOpen && (
        <div className="reason-modal-overlay" onClick={closeImageEditModal}>
          <div
            className="reason-modal-content image-edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-title-section">
                <h2 className="modal-product-title">Edit Images</h2>
                <h3 className="modal-analysis-type">
                  Drag to reorder / swap. Rotate if needed. Remove (✕) any
                  unwanted or broken image. First image will be the thumbnail.
                </h3>
              </div>
            </div>
            <div className="modal-body">
              <div className="image-edit-grid">
                {editingImages.map((img, idx) => (
                  <div
                    key={img.id}
                    draggable
                    onDragStart={handleModalImageDragStart(img.id)}
                    onDragOver={handleModalImageDragOver(img.id)}
                    onDragLeave={handleModalImageDragLeave}
                    onDrop={handleModalImageDrop(img.id)}
                    onDragEnd={handleModalImageDragEnd}
                    className={`image-edit-card ${
                      draggedImageId === img.id ? "preview-card--dragging" : ""
                    } ${
                      dragOverImageId === img.id && draggedImageId !== img.id
                        ? "preview-card--drag-over"
                        : ""
                    }`}
                  >
                    {idx === 0 && (
                      <span className="thumbnail-badge">Thumbnail</span>
                    )}
                    <img
                      src={img.url}
                      alt={`Product view ${idx + 1}`}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = PLACEHOLDER_IMG;
                      }}
                    />
                    <p className="image-edit-order">{idx + 1}</p>
                    <div className="image-edit-actions">
                      <button
                        type="button"
                        className="image-edit-rotate-btn"
                        onClick={() => rotateImageInModal(idx, -90)}
                        title="Rotate left"
                      >
                        ↺
                      </button>
                      <button
                        type="button"
                        className="image-edit-rotate-btn"
                        onClick={() => rotateImageInModal(idx, 90)}
                        title="Rotate right"
                      >
                        ↻
                      </button>
                      <button
                        type="button"
                        className="image-edit-rotate-btn"
                        onClick={() => handleRemoveImage(img.id)}
                        title="Remove this image"
                        style={{ background: "#dc3545" }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn-close modal-btn-secondary"
                onClick={closeImageEditModal}
                disabled={imageEditSaving}
              >
                Cancel
              </button>
              <button
                className="modal-btn-close"
                onClick={handleSaveImageEdits}
                disabled={imageEditSaving || editingImages.length === 0}
              >
                {imageEditSaving ? "Saving..." : "Save Images"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-5 text-center">
          <h4>Loading products...</h4>
        </div>
      ) : (
        <>
          {paginatedProducts.length > 0 && (
            <div className="select-all-row">
              <input
                type="checkbox"
                id="selectAll"
                checked={
                  paginatedProducts.length > 0 &&
                  paginatedProducts.every((p) =>
                    selectedProductIds.includes(p.id),
                  )
                }
                onChange={handleSelectAll}
              />
              <label htmlFor="selectAll">Select All on Page</label>
            </div>
          )}

          <div className="product-list">
            {paginatedProducts.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-box-open"></i>
                <p>
                  {error
                    ? "Unable to load products."
                    : "No products found matching your filters."}
                </p>
              </div>
            ) : (
              paginatedProducts.map((product) => {
                const orderInfo = orderStatusMap[product.id];
                const isSold =
                  orderInfo &&
                  orderInfo.seller_status !== "not_available" &&
                  orderInfo.qc_status !== "rejected";
                return (
                  <div
                    key={product.id}
                    className={`product-card ${
                      selectedProductIds.includes(product.id) ? "selected" : ""
                    } ${isSold ? "sold" : ""}`}
                  >
                    <div className="product-main">
                      <div className="product-main-content">
                        <div className="product-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedProductIds.includes(product.id)}
                            onChange={() => handleSelectProduct(product.id)}
                          />
                        </div>

                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <RetryImage
                            src={product.images[0]}
                            alt={product.title}
                            className="product-image"
                          />
                          {orderStatusMap[product.id] &&
                            (() => {
                              const st = orderStatusMap[product.id];
                              const isRejected =
                                st.seller_status === "not_available" ||
                                st.qc_status === "rejected";
                              if (isRejected) return null;
                              return <div className="sold-badge">✓ SOLD</div>;
                            })()}
                        </div>

                        <div className="product-info">
                          <h5 className="product-title">{product.title}</h5>
                          <p className="product-description">
                            {product.description}
                          </p>

                          <div className="product-badges">
                            <span className="product-badge">
                              Price:{" "}
                              {formatPriceInner(product.price || 0, product)}
                            </span>
                            <span className="product-badge">
                              Category: {product.category || "Uncategorized"}
                            </span>
                            <span className="product-badge">
                              Condition:{" "}
                              {product.condition ||
                                product.marketplaceDetails?.ebay?.Condition ||
                                "Not specified"}
                            </span>
                            <span className="product-badge">
                              Quantity: {product.quantity || 0}
                            </span>
                            {(userStoreMap[product.uploadedBy?.id] ||
                              product.uploadedBy?.name) && (
                              <span className="product-badge">
                                Seller:{" "}
                                {userStoreMap[product.uploadedBy?.id] ||
                                  product.uploadedBy?.name}
                              </span>
                            )}
                          </div>

                          <div className="marketplace-badges">
                            {product.selectedMarketplaces.map((id) => {
                              const market = marketplaces.find(
                                (m) => m.id === id,
                              );
                              if (id === "local") return null;
                              return (
                                <span key={id} className="marketplace-badge">
                                  {market?.name || id}
                                </span>
                              );
                            })}

                            {product.status && (
                              <span
                                className={`status-badge status-${product.status
                                  .toLowerCase()
                                  .replace(/\s+/g, "_")}`}
                              >
                                {product.status
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (c) => c.toUpperCase())}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="product-actions">
                          <button
                            type="button"
                            className="btn btn-info btn-sm product-action-btn"
                            onClick={() => handleToggleExpand(product.id)}
                          >
                            {expandedProduct === product.id
                              ? "Collapse"
                              : "Expand"}
                          </button>

                          { <button
                            type="button"
                            className="btn btn-danger btn-sm product-action-btn"
                            onClick={() =>
                              navigate(`/products/${product.id}`, {
                                state: { product },
                              })
                            }
                          >
                            View Details
                          </button> }

                          {isEditor && (
                            <button
                              type="button"
                              className="btn btn-warning btn-sm product-action-btn"
                              onClick={() =>
                                navigate(`/products/update/${product.id}`)
                              }
                            >
                              Update
                            </button>
                          )}

                          {canEditImages && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm product-action-btn"
                              onClick={() => startEditImages(product)}
                              title="Edit images: rotate, reorder, swap"
                              disabled={validatingProductId === product.id}
                            >
                              {validatingProductId === product.id
                                ? "Checking..."
                                : "Edit Images"}
                            </button>
                          )}

                          {isEditor && (
                            <button
                              type="button"
                              className="btn btn-success btn-sm product-action-btn"
                              onClick={async () => {
                                try {
                                  const sellerUserId = product.uploadedBy?.id;
                                  const existingSku = product.sku;

                                  let finalSku = existingSku;
                                  try {
                                    const res = await fetch(
                                      `${BASE_URL}/api/v1/checkAndGetSku?user_id=${encodeURIComponent(
                                        sellerUserId,
                                      )}&existing_sku=${encodeURIComponent(
                                        existingSku,
                                      )}`,
                                    );
                                    const data = await res.json();
                                    if (data.success) {
                                      finalSku = data.sku;
                                    }
                                  } catch (err) {
                                    console.error("SKU check failed:", err);
                                  }

                                  let fullProductData = null;
                                  try {
                                    const token = localStorage.getItem("token");
                                    const fullRes = await fetch(
                                      `${BASE_URL}/api/product-full/${product.id}`,
                                      {
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                        },
                                      },
                                    );
                                    const fullJson = await fullRes.json();
                                    if (fullJson.success && fullJson.data) {
                                      fullProductData = fullJson.data;
                                    }
                                  } catch (err) {
                                    console.error(
                                      "Full product fetch failed:",
                                      err,
                                    );
                                  }

                                  const { product: fullProduct, ebay } =
                                    fullProductData || {};

                                  let savedFormData = {};
                                  if (fullProduct?.raw_response) {
                                    try {
                                      let raw = fullProduct.raw_response;
                                      if (typeof raw === "string")
                                        raw = JSON.parse(raw);
                                      if (raw.formData)
                                        savedFormData = raw.formData;
                                    } catch (err) {
                                      console.warn(
                                        "Failed to parse raw_response",
                                        err,
                                      );
                                    }
                                  }

                                  const ebayFields = {};
                                  if (ebay && ebay.aspects) {
                                    let aspects = ebay.aspects;
                                    if (typeof aspects === "string")
                                      aspects = JSON.parse(aspects);
                                    Object.entries(aspects).forEach(
                                      ([key, value]) => {
                                        let val = Array.isArray(value)
                                          ? value.join(", ")
                                          : value;
                                        if (
                                          val &&
                                          val !== "N/A" &&
                                          val.trim() !== ""
                                        ) {
                                          let mappedKey = key;
                                          if (key === "Colour")
                                            mappedKey = "Color";
                                          ebayFields[mappedKey] = val;
                                        }
                                      },
                                    );
                                  }

                                  const finalBrand =
                                    ebayFields.Brand ||
                                    savedFormData.brand ||
                                    fullProduct?.brand ||
                                    "";
                                  const finalColor =
                                    ebayFields.Color ||
                                    savedFormData.color ||
                                    fullProduct?.color ||
                                    "";
                                  const finalMaterial =
                                    ebayFields.Material ||
                                    savedFormData.material ||
                                    fullProduct?.material ||
                                    "";
                                  const finalTags =
                                    savedFormData.tags ||
                                    fullProduct?.tags ||
                                    "";
                                  const finalVariants = savedFormData.variants
                                    ?.length
                                    ? savedFormData.variants
                                    : [
                                        {
                                          id: 1,
                                          size: ebayFields.Size || "",
                                          color: finalColor,
                                          sku: "",
                                        },
                                      ];
                                  const finalCategoryAspects =
                                    savedFormData.categoryAspects || [];
                                  const finalAllCategoryAspects =
                                    savedFormData.allCategoryAspects || [];
                                  const finalCategoryId =
                                    savedFormData.categoryId || "";
                                  const finalConditionScore =
                                    savedFormData.condition_score || "";
                                  const finalConditionSummary =
                                    savedFormData.condition_summary || "";
                                  const finalAuthenticityScore =
                                    savedFormData.authenticity_score || "";
                                  const finalAuthenticitySummary =
                                    savedFormData.authenticity_summary || "";
                                  const finalVintedSize =
                                    savedFormData.vintedSize ||
                                    ebayFields.Size ||
                                    "";

                                  const formData = {
                                    currentStep: 2,
                                    formData: {
                                      uploadedImages: fullProduct?.images
                                        ?.length
                                        ? fullProduct.images
                                        : product.images?.filter(
                                            (img) =>
                                              !img.includes("placeholder"),
                                          ) || [],
                                      blurStatuses: [],
                                      imageProcessing: false,
                                      backgroundRemoval: false,
                                      blurDetection: false,
                                      upscaleImage: false,
                                      title:
                                        fullProduct?.title ||
                                        product.title ||
                                        "",
                                      description:
                                        fullProduct?.description ||
                                        product.description ||
                                        "",
                                      sku: finalSku,
                                      _existing_product_id: product.id,
                                      price: String(
                                        fullProduct?.price ??
                                          product.price ??
                                          "",
                                      ),
                                      quantity: String(
                                        fullProduct?.quantity ??
                                          product.quantity ??
                                          "1",
                                      ),
                                      category:
                                        savedFormData.category ||
                                        product.category ||
                                        "",
                                      condition:
                                        fullProduct?.condition ||
                                        product.condition ||
                                        "",
                                      brand: finalBrand,
                                      color: finalColor,
                                      material: finalMaterial,
                                      tags: finalTags,
                                      metafields:
                                        savedFormData.metafields || {},
                                      categoryAspects: finalCategoryAspects,
                                      allCategoryAspects:
                                        finalAllCategoryAspects,
                                      categoryId: finalCategoryId,
                                      condition_score: finalConditionScore,
                                      condition_summary: finalConditionSummary,
                                      authenticity_score:
                                        finalAuthenticityScore,
                                      authenticity_summary:
                                        finalAuthenticitySummary,
                                      variants: finalVariants,
                                      vintedSize: finalVintedSize,
                                      selectedMarketplaces:
                                        product.selectedMarketplaces?.filter(
                                          (m) => m !== "local",
                                        ) || [],
                                      poshmarkCategory:
                                        savedFormData.poshmarkCategory ||
                                        "Women",
                                      poshmarkSubCategory:
                                        savedFormData.poshmarkSubCategory || "",
                                      vintedCategory:
                                        savedFormData.vintedCategory || "",
                                      vintedCategoryId:
                                        savedFormData.vintedCategoryId || "",
                                      vintedCategoryPath:
                                        savedFormData.vintedCategoryPath || [],
                                      vintedPackageSize:
                                        savedFormData.vintedPackageSize ||
                                        "medium",
                                      seller_id: sellerUserId,
                                    },
                                  };

                                  const copyId = `copy_${Date.now()}_${Math.random()
                                    .toString(36)
                                    .substring(2, 8)}`;
                                  sessionStorage.setItem(
                                    copyId,
                                    JSON.stringify(formData),
                                  );
                                  window.open(
                                    `/products/add?copyId=${copyId}`,
                                    "_blank",
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error preparing product copy:",
                                    error,
                                  );
                                  alert("Kuch galat ho gaya, dubara try karo.");
                                }
                              }}
                            >
                              Add Product
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {expandedProduct === product.id && (
                      <div className="expanded-details">
                        {product.selectedMarketplaces.map((marketplaceId) => {
                          const market = marketplaces.find(
                            (m) => m.id === marketplaceId,
                          );
                          const details =
                            product.marketplaceDetails[marketplaceId];
                          if (!details || Object.keys(details).length === 0)
                            return null;

                          return (
                            <div
                              key={marketplaceId}
                              className="marketplace-details-section"
                            >
                              <h6
                                className={`marketplace-title text-${market?.color || "secondary"}`}
                              >
                                {market?.name || marketplaceId.toUpperCase()}{" "}
                                Details
                              </h6>
                              <div className="details-grid">
                                {Object.entries(details)
                                  .filter(
                                    ([, value]) =>
                                      value &&
                                      value !== "" &&
                                      value !== "Not specified" &&
                                      value !== "Not listed" &&
                                      value !== "Not synced",
                                  )
                                  .map(([key, value]) => (
                                    <div key={key} className="detail-item">
                                      <span className="detail-label">
                                        {key}
                                      </span>
                                      <span className="detail-value">
                                        {typeof value === "object"
                                          ? JSON.stringify(value)
                                          : String(value)}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {totalPages > 1 && (
            <>
              <div className="pagination">
                <div
                  className={`page-item ${safePage === 1 ? "disabled" : ""}`}
                >
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => handlePageChange(safePage - 1)}
                    disabled={safePage === 1}
                  >
                    Previous
                  </button>
                </div>

                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (safePage <= 3) {
                    pageNum = i + 1;
                  } else if (safePage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = safePage - 2 + i;
                  }

                  return (
                    <div
                      key={pageNum}
                      className={`page-item ${
                        safePage === pageNum ? "active" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </div>
                  );
                })}

                <div
                  className={`page-item ${
                    safePage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => handlePageChange(safePage + 1)}
                    disabled={safePage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
              <div className="pagination-info">
                Page {safePage} of {totalPages} ({filteredProducts.length}{" "}
                items)
              </div>
            </>
          )}
        </>
      )}
    </MDBContainer>
  );
};

export default ViewProductsPage;
